import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Loader2, User } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import DirectorRoleCard from './roles/director-role-card';
import ShareholderRoleCard from './roles/shareholder-role-card';
import TrusteeRoleCard from './roles/trustee-role-card';
import BeneficiaryRoleCard from './roles/beneficiary-role-card';
import DealRoleCard from './roles/deal-role-card';

interface ContactRolesPanelProps {
  contactId: string;
  contactLabel?: string;
}

interface ContactRolesResponse {
  directors: Array<{
    id: string;
    accountId: string;
    accountName: string;
    entityType: string;
    role: 'director';
    appointmentDate: string | null;
    ceasedDate: string | null;
    isActive: boolean;
  }>;
  shareholders: Array<{
    id: string;
    accountId: string;
    accountName: string;
    entityType: string;
    role: 'shareholder';
    shareClass: string | null;
    numberOfShares: number | null;
    ownershipPercentage: number | null;
    appointmentDate: string | null;
    ceasedDate: string | null;
    isBeneficialOwner: boolean;
    isActive: boolean;
  }>;
  trustees: Array<{
    id: string;
    accountId: string;
    accountName: string;
    entityType: string;
    role: 'trustee';
    appointmentDate: string | null;
    ceasedDate: string | null;
    isActive: boolean;
  }>;
  beneficiaries: Array<{
    id: string;
    trustAccountId: string;
    trustName: string;
    beneficiaryClass: string | null;
    unitPercentage: number | null;
    isBeneficialOwner: boolean;
    isActive: boolean;
  }>;
  dealRoles: Array<{
    id: string;
    dealId: string;
    dealReference: string;
    dealName: string;
    dealStatus: string;
    role: string;
    source: string;
    associatedAccountId: string | null;
    associatedAccountName: string | null;
    associationRole: string | null;
    createdAt: string;
  }>;
  summary: {
    totalActiveRoles: number;
    totalCeasedRoles: number;
    directorCount: number;
    shareholderCount: number;
    trusteeCount: number;
    beneficiaryCount: number;
    dealRoleCount: number;
  };
}

export default function ContactRolesPanel({ contactId, contactLabel }: ContactRolesPanelProps) {
  const [showCeased, setShowCeased] = useState(false);

  const { data, isLoading, error } = useQuery<ContactRolesResponse>({
    queryKey: ["/api/crm/contacts", contactId, "roles", { includeCeased: String(showCeased) }],
  });

  if (isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <p className="text-sm text-muted-foreground">Failed to load roles and relationships</p>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!data) {
    return null;
  }

  const { directors, shareholders, trustees, beneficiaries, dealRoles, summary } = data;

  const hasNoActiveRoles = summary.totalActiveRoles === 0;
  const hasOnlyCeasedRoles = hasNoActiveRoles && summary.totalCeasedRoles > 0;

  if (hasNoActiveRoles && !showCeased) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col items-center justify-center py-8 text-center">
            <User className="h-12 w-12 text-muted-foreground mb-3" />
            <p className="text-sm text-muted-foreground mb-3">
              {hasOnlyCeasedRoles
                ? 'No active roles. Toggle to view historical relationships.'
                : 'No roles or relationships found for this contact.'}
            </p>
            {hasOnlyCeasedRoles && (
              <Button onClick={() => setShowCeased(true)} size="sm">
                Show ceased roles
              </Button>
            )}
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-4">
      {/* Toggle for ceased roles */}
      {summary.totalCeasedRoles > 0 && (
        <Card>
          <CardContent className="pt-3">
            <div className="flex items-center space-x-2">
              <Switch
                id="show-ceased"
                checked={showCeased}
                onCheckedChange={setShowCeased}
              />
              <Label htmlFor="show-ceased" className="text-sm">
                Show ceased roles ({summary.totalCeasedRoles})
              </Label>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Directors */}
      {directors.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            DIRECTORS ({directors.length})
          </h3>
          <div className="space-y-2">
            {directors.map((director) => (
              <DirectorRoleCard key={director.id} director={director} />
            ))}
          </div>
        </div>
      )}

      {/* Shareholders */}
      {shareholders.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            SHAREHOLDERS ({shareholders.length})
          </h3>
          <div className="space-y-2">
            {shareholders.map((shareholder) => (
              <ShareholderRoleCard key={shareholder.id} shareholder={shareholder} />
            ))}
          </div>
        </div>
      )}

      {/* Trustees */}
      {trustees.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            TRUSTEES ({trustees.length})
          </h3>
          <div className="space-y-2">
            {trustees.map((trustee) => (
              <TrusteeRoleCard key={trustee.id} trustee={trustee} />
            ))}
          </div>
        </div>
      )}

      {/* Trust Beneficiaries */}
      {beneficiaries.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            TRUST BENEFICIARIES ({beneficiaries.length})
          </h3>
          <div className="space-y-2">
            {beneficiaries.map((beneficiary) => (
              <BeneficiaryRoleCard key={beneficiary.id} beneficiary={beneficiary} />
            ))}
          </div>
        </div>
      )}

      {/* Deal Roles */}
      {dealRoles.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-xs font-semibold text-muted-foreground">
            DEAL ROLES ({dealRoles.length})
          </h3>
          <div className="space-y-2">
            {dealRoles.map((dealRole) => (
              <DealRoleCard key={dealRole.id} dealRole={dealRole} contactId={contactId} contactLabel={contactLabel} />
            ))}
          </div>
        </div>
      )}

      {/* Empty state when all filtered out */}
      {directors.length === 0 &&
        shareholders.length === 0 &&
        trustees.length === 0 &&
        beneficiaries.length === 0 &&
        dealRoles.length === 0 && (
          <Card>
            <CardContent className="pt-6">
              <div className="flex flex-col items-center justify-center py-8 text-center">
                <p className="text-sm text-muted-foreground">No active roles found</p>
              </div>
            </CardContent>
          </Card>
        )}
    </div>
  );
}
